'use client'
import React, { useImperativeHandle, useRef } from 'react'
import { Heading, Picture, Text } from '../../components/index.js'
import { cn } from '../../helpers/utils.js'
import { withLayout } from '../../shared/Styles.js'
import { useExposure } from '../../hooks/useExposure.js'
import type { ImageTextFeatureProps, ImageTextFeatureItem } from './types.js'

const componentType = 'image'
const componentName = 'image_text_feature'

const ImageTextFeature = React.forwardRef<HTMLDivElement, ImageTextFeatureProps>(({ data, className }, ref) => {
  const { title, subtitle, pcImage, mobileImage, padImage, items = [], imagePosition = 'left', theme = 'light' } = data

  const boxRef = useRef<HTMLDivElement>(null)

  useExposure(boxRef, {
    componentType,
    componentName,
    componentTitle: title,
    componentDescription: subtitle,
  })

  useImperativeHandle(ref, () => boxRef.current as HTMLDivElement)

  return (
    <section
      ref={boxRef}
      data-ui-component-id="ImageTextFeature"
      className={cn(
        'image-text-feature',
        theme === 'dark' ? 'aiui-dark' : '',
        'text-info-primary laptop:flex-row laptop:gap-[32px] desktop:gap-[48px] lg-desktop:gap-[64px] flex flex-col items-center gap-[24px]',
        className
      )}
    >
      {/* 图片区域 */}
      <Picture
        source={`${pcImage?.url}, ${padImage?.url || pcImage?.url} 1024, ${mobileImage?.url || pcImage?.url} 768`}
        alt={pcImage?.alt}
        className={cn(
          'image-text-feature__image tablet:aspect-[704/360] laptop:aspect-[824/560] aspect-[716/720] max-h-[560px] max-w-[824px] shrink-0',
          {
            'image-text-feature__image--left order-2 laptop:order-1 laptop:basis-[63%] desktop:basis-[57%]':
              imagePosition === 'left',
            'image-text-feature__image--right order-2 laptop:basis-[63%] desktop:basis-[57%]':
              imagePosition === 'right',
          }
        )}
      />

      {/* 内容区域 */}
      <div
        className={cn('image-text-feature__content laptop:w-fit flex w-full flex-col items-start justify-center', {
          'order-1 laptop:order-2': imagePosition === 'left',
          'order-1': imagePosition === 'right',
        })}
      >
        {/* 标题 */}
        <Heading size={4} html={title} as="h3" className="image-text-feature__title w-full text-left" />

        {/* 副标题 */}
        {subtitle && (
          <Text
            size={4}
            html={subtitle}
            as="p"
            className="image-text-feature__subtitle min-xxl:mt-[8px] laptop:text-[16px] lg-desktop:text-[18px] mt-[4px] text-left text-[14px]"
          />
        )}

        {/* 功能列表 */}
        <div className="image-text-feature__items tablet:flex-row tablet:justify-start tablet:gap-[90px] laptop:w-fit laptop:flex-col laptop:justify-around laptop:gap-0 flex w-full flex-col justify-around gap-0">
          {items.map((item: ImageTextFeatureItem, index: number) => (
            <div key={index} className="image-text-feature__item laptop:mt-[32px] desktop:mt-[48px] mt-[24px]">
              <div className="image-text-feature__item-header flex flex-row items-center gap-[8px]">
                <img
                  src={item.icon}
                  alt="icon"
                  className="image-text-feature__item-icon min-md:text-[40px] min-l:text-[40px] min-xl:text-[56px] min-xxl:text-[64px] desktop:h-[44px] lg-desktop:h-[52px] h-[28px] -translate-y-[12%] md:text-[40px]"
                />
                <Heading
                  size={5}
                  as="h6"
                  className="image-text-feature__item-text bg-gradient-to-r from-[#3ad1ff] to-[#008cd6] bg-clip-text text-transparent"
                >
                  {item.text}
                </Heading>
              </div>
              <Text
                size={4}
                as="p"
                html={item.desc}
                className="image-text-feature__item-desc tablet:text-[14px] laptop:text-[14px] desktop:text-[16px] lg-desktop:text-[18px] -mt-[2px] text-[14px]"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
})

ImageTextFeature.displayName = 'ImageTextFeature'

export default withLayout(ImageTextFeature)
